import React, { PropsWithChildren } from "react";
import { MAuswahlwert, MDetailSerie, MZubehoer } from "../../../metamodell";
import { PAuswahlwert, PDetailSerie, PProfilkategorie } from "../../../metamodell_paths";
import { Farbmenuselect } from "../zauneditor/Farbmenuselect";
import {
  filterPathIntersectionCached,
  findPathIntersectionCached,
} from "oc-app/metadata/GraphMetadata";
import { elementOrTor } from "../../arrangement/elementFunctions";
import { VisualPropsOfAuswahlwert } from "./nodes/metadata/Auswahlwert";
import { Auswahl } from "../Auswahl";
import { NumberSelect } from "../NumberSelect";

type UpdateableAuswahlwert = {
  auswahlwert: MAuswahlwert;
  updatedProps: VisualPropsOfAuswahlwert;
};

function WrapWithEditor({ children }: PropsWithChildren<{}>) {
  return (
    <div
      id={"cfg-app"}
      className={"cfg-page-Zauneditor"}
      style={{ display: "block", height: "auto", width: "auto", fontSize: "large" }}
    >
      {children}
    </div>
  );
}

function WrapWithOptions({ children }: PropsWithChildren<{}>) {
  return (
    <div className="editor-optionen">
      <div className="options-tab">
        <div className="flex column-container">{children}</div>
      </div>
    </div>
  );
}

function WrapWithFüllungmenu({ children }: PropsWithChildren<{}>) {
  return <div className={"fuellung-menu"}>{children}</div>;
}

function VorschauFüllungen({ auswahlwert, updatedProps }: UpdateableAuswahlwert) {
  const liveAuswahlwert = { ...auswahlwert, ...updatedProps } as MAuswahlwert;
  const füllungAuswahlen = new Set([liveAuswahlwert]);
  const detailSerie = findPathIntersectionCached("AuswahlBildvorschau.tsx:44")(
    auswahlwert,
    (p: PAuswahlwert) => elementOrTor(p.Objekt).DetailSerie
  );
  if (detailSerie) {
    (
      filterPathIntersectionCached("AuswahlBildvorschau.tsx:46")(
        detailSerie,
        (d: PDetailSerie) => elementOrTor(d).Füllung.Auswahlwert
      ) as MAuswahlwert[]
    ).forEach((a) => {
      if (füllungAuswahlen.size < 4 && a !== auswahlwert) {
        füllungAuswahlen.add(a);
      }
    });
  }

  const dekorAuswahlen = new Set([liveAuswahlwert]);
  const profilkategorie = findPathIntersectionCached("AuswahlBildvorschau.tsx:54")(
    auswahlwert,
    (p: PAuswahlwert) => p.Objekt.Profilkategorie
  );
  if (profilkategorie) {
    (
      filterPathIntersectionCached("AuswahlBildvorschau.tsx:59")(
        profilkategorie,
        (p: PProfilkategorie) => p.Profile.Auswahlwert
      ) as MAuswahlwert[]
    ).forEach((a) => {
      if (dekorAuswahlen.size < 4 && a != auswahlwert) {
        dekorAuswahlen.add(a);
      }
    });
  }

  return (
    <WrapWithEditor>
      {füllungAuswahlen.size > 1 && (
        <section aria-labelledby="fuellungen-title">
          <h3 id="fuellungen-title">{I18N("Füllungen")}</h3>
          <WrapWithFüllungmenu>
            <div
              className="füllungen"
              data-select-key="Füllung"
              data-value={auswahlwert.Key}
              role="group"
              aria-labelledby="fuellungen-title"
            >
              <Farbmenuselect
                auswahlwerte={[...füllungAuswahlen]}
                value={liveAuswahlwert}
                onChange={(_) => {}}
                className="material-item"
              />
            </div>
          </WrapWithFüllungmenu>
        </section>
      )}
      {dekorAuswahlen.size > 1 && (
        <section aria-labelledby="dekorprofile-title">
          <h3 id="dekorprofile-title">{I18N("Dekorprofile")}</h3>
          <WrapWithOptions>
            <Farbmenuselect
              auswahlwerte={[...dekorAuswahlen]}
              value={liveAuswahlwert}
              onChange={(_) => {}}
              className="pfosten-item"
            />
          </WrapWithOptions>
        </section>
      )}
    </WrapWithEditor>
  );
}

function VorschauPDF({ auswahlwert, updatedProps }: UpdateableAuswahlwert) {
  const liveAuswahlwert = { ...auswahlwert, ...updatedProps };
  return (
    <WrapWithEditor>
      <section aria-labelledby="pdf-title">
        <h3 id="pdf-title">{I18N("PDF")}</h3>
        <div
          style={{
            background: "white",
            padding: "1rem",
            textAlign: "right",
            width: "calc(378px + 2rem)",
          }}
          role="region"
          aria-label={I18N("aria::PDF-Vorschau")}
        >
          <img
            src={liveAuswahlwert.Auswahlbild}
            style={{ width: "95px" }}
            alt={liveAuswahlwert.Name}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{auswahlwert.Auswahl.Key}</span>
            <span>{liveAuswahlwert.Name}</span>
          </div>
        </div>
      </section>
    </WrapWithEditor>
  );
}

function VorschauDetailserie({ auswahlwert, updatedProps }: UpdateableAuswahlwert) {
  const liveAuswahlwert = { ...auswahlwert, ...updatedProps } as MAuswahlwert;
  const auswahlen = new Set([liveAuswahlwert]);
  (
    filterPathIntersectionCached("AuswahlBildvorschau.tsx:126")(
      auswahlwert,
      (o: PAuswahlwert) => o.Objekt.Serie.DetailSerie.Auswahlwert
    ) as MAuswahlwert[]
  ).forEach((a) => {
    if (a != auswahlwert) {
      auswahlen.add(a);
    }
  });
  return (
    <WrapWithEditor>
      <section aria-labelledby="detailserie-title">
        <h3 id="detailserie-title">{I18N("aria::Detailserie in Füllungsauswahl")}</h3>
        <WrapWithFüllungmenu>
          <div className="detailserien-auswahl" role="group" aria-labelledby="detailserie-title">
            <Auswahl
              auswahl={auswahlwert.Auswahl}
              value={liveAuswahlwert}
              showHeadline={false}
              auswahlwerte={[...auswahlen]}
              onChange={(_) => {}}
            />
          </div>
        </WrapWithFüllungmenu>
      </section>
    </WrapWithEditor>
  );
}

function VorschauDropdown({ auswahlwert, updatedProps }: UpdateableAuswahlwert) {
  const liveAuswahlwert = { ...auswahlwert, ...updatedProps } as MAuswahlwert;
  const auswahlen = new Set([liveAuswahlwert]);
  (
    filterPathIntersectionCached("AuswahlBildvorschau.tsx:155")(
      auswahlwert,
      (o: PAuswahlwert) => o.Auswahl.Auswahlwert
    ) as MAuswahlwert[]
  ).forEach((a) => {
    if (a != auswahlwert) {
      auswahlen.add(a);
    }
  });
  return (
    <WrapWithEditor>
      <section aria-labelledby="dropdown-title">
        <h3 id="dropdown-title">{I18N("Dropdown")}</h3>
        <WrapWithOptions>
          <Auswahl
            auswahl={auswahlwert.Auswahl}
            small={true}
            auswahlwerte={[...auswahlen]}
            disableUnzulässigeAuswahlwerte={true}
            value={liveAuswahlwert}
            onChange={(_) => {}}
          />
        </WrapWithOptions>
      </section>
    </WrapWithEditor>
  );
}

function VorschauFarbe({ auswahlwert, updatedProps }: UpdateableAuswahlwert) {
  const liveAuswahlwert = { ...auswahlwert, ...updatedProps } as MAuswahlwert;
  const auswahlen = new Set([liveAuswahlwert]);
  (
    filterPathIntersectionCached("AuswahlBildvorschau.tsx:180")(
      auswahlwert,
      (o: PAuswahlwert) => o.Auswahl.Auswahlwert
    ) as MAuswahlwert[]
  ).forEach((a) => {
    if (auswahlen.size < 4 && a != auswahlwert) {
      auswahlen.add(a);
    }
  });
  return (
    <WrapWithEditor>
      <section aria-labelledby="farbauswahl-title">
        <h3 id="farbauswahl-title">{I18N("Farbauswahl")}</h3>
        <WrapWithOptions>
          <Farbmenuselect
            auswahlwerte={[...auswahlen]}
            value={liveAuswahlwert}
            onChange={(_) => {}}
            className="pfosten-item"
          />
        </WrapWithOptions>
      </section>
    </WrapWithEditor>
  );
}

function VorschauEAntrieb({ auswahlwert, updatedProps }: UpdateableAuswahlwert) {
  const liveAuswahlwert = { ...auswahlwert, ...updatedProps } as MAuswahlwert;
  const found = findPathIntersectionCached("AuswahlBildvorschau.tsx:202")(
    auswahlwert,
    (a: PAuswahlwert) => a.Zubehoer
  ) as MZubehoer;
  const { MinAnzahl = 0, MaxAnzahl = 0 } = found;

  return (
    <WrapWithEditor>
      <section aria-labelledby="eantrieb-title">
        <h3 id="eantrieb-title">{I18N("E-Antrieb Zubehör")}</h3>
        <WrapWithOptions>
          <NumberSelect
            key={liveAuswahlwert.Key}
            title={liveAuswahlwert.Name}
            auswahlwert={liveAuswahlwert}
            minAnzahl={MinAnzahl}
            maxAnzahl={MaxAnzahl}
            value={Math.floor((MaxAnzahl - MinAnzahl) / 2)}
            onChange={(_) => {}}
          />
        </WrapWithOptions>
      </section>
    </WrapWithEditor>
  );
}

export function AuswahlBildvorschau(
  auswahlwert: MAuswahlwert,
  updatedProps: VisualPropsOfAuswahlwert
) {
  const auswahl = auswahlwert.Auswahl.Key;
  const showFüllung = auswahl == "Füllung";
  const showFarbauswahl = ["Pfostenfarbe"].includes(auswahl);
  const showEAntrieb = auswahl == "E_Antrieb_Zubehör";
  const showDetailserie = auswahl == "DetailSerie";
  const showDropdown = [showFüllung, showFarbauswahl, showEAntrieb, showDetailserie].every(
    (c) => !c
  );
  const showPDF = ["Füllung", "Rahmen"].includes(auswahl);

  return (
    <>
      {showFüllung && <VorschauFüllungen auswahlwert={auswahlwert} updatedProps={updatedProps} />}
      {showDetailserie && (
        <VorschauDetailserie auswahlwert={auswahlwert} updatedProps={updatedProps} />
      )}
      {showFarbauswahl && <VorschauFarbe auswahlwert={auswahlwert} updatedProps={updatedProps} />}
      {showEAntrieb && <VorschauEAntrieb auswahlwert={auswahlwert} updatedProps={updatedProps} />}
      {showDropdown && <VorschauDropdown auswahlwert={auswahlwert} updatedProps={updatedProps} />}
      {showPDF && <VorschauPDF auswahlwert={auswahlwert} updatedProps={updatedProps} />}
    </>
  );
}
